import React, { useState } from "react";
import HRHeader from "../components/HRHeader";
import api, { downloadContractSheet, downloadSenioritySheet } from "../api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Settings, User, Mail, Phone, MapPin, Lock, Save, ShieldCheck, Building2, FileText, History, BadgeCheck } from "lucide-react";

const ROLE_LABEL = { admin: "Amministratore HR", responsabile: "Responsabile", dipendente: "Dipendente" };

const ProfiloPage = () => {
  const { user, refreshUser, isExternal } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [address, setAddress] = useState(user?.address || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [downloadingAtt, setDownloadingAtt] = useState(false);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const downloadContract = async () => {
    try { await downloadContractSheet(user.id, `scheda_contratto_${user.matricola}.pdf`); toast.success("Scheda contratto scaricata"); }
    catch { toast.error("Errore durante il download"); }
  };

  const downloadSeniority = async () => {
    try { await downloadSenioritySheet(user.id, `anzianita_${user.matricola}.pdf`); toast.success("Riepilogo anzianità scaricato"); }
    catch { toast.error("Errore durante il download"); }
  };

  const downloadAttestato = async () => {
    setDownloadingAtt(true);
    try {
      const res = await api.get("/certificates/servizio/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `attestato_servizio_${user.matricola}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Attestato di servizio scaricato");
    } catch (err) {
      let msg = "Errore durante il download";
      try { const t = await err?.response?.data?.text?.(); if (t) msg = JSON.parse(t).detail || msg; } catch { /* noop */ }
      toast.error(msg);
    } finally { setDownloadingAtt(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/profile", { email, phone, address });
      await refreshUser();
      toast.success("Profilo aggiornato!");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSavingProfile(false); }
  };

  const savePwd = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("La nuova password deve avere almeno 6 caratteri"); return; }
    if (next !== confirm) { toast.error("Le password non coincidono"); return; }
    setSavingPwd(true);
    try {
      await api.post("/profile/password", { current_password: current, new_password: next });
      toast.success("Password aggiornata!");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSavingPwd(false); }
  };

  const inputCls = "w-full border border-gray-300 rounded-sm pl-9 pr-3 py-2.5 text-[14px] outline-none focus:border-[#16345c]";

  return (
    <div className="min-h-screen bg-[#f0f2f5]" data-testid="profilo-page">
      <HRHeader />
      <main className="max-w-[1000px] mx-auto px-4 py-8">
        <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><Settings size={24} /> Profilo e Impostazioni</h1>
        <p className="text-[14px] text-gray-500 mb-6">Gestisci i tuoi dati personali e le credenziali di accesso.</p>

        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 flex items-center gap-5">
          <span className="w-20 h-20 rounded-full bg-[#16345c] text-white flex items-center justify-center text-[26px] font-black shrink-0">{initials}</span>
          <div>
            <div className="text-[22px] font-black text-[#16345c]">{user?.name}</div>
            <div className="text-[13px] text-gray-500 flex items-center gap-2 flex-wrap mt-1">
              <span className="flex items-center gap-1">{user?.role === "admin" && <ShieldCheck size={13} className="text-[#009640]" />}{ROLE_LABEL[user?.role]}</span>
              <span className="flex items-center gap-1"><User size={13} /> Matricola {user?.matricola}</span>
              <span className="flex items-center gap-1"><Building2 size={13} /> {user?.dept}</span>
            </div>
          </div>
          <div className="ml-auto flex flex-wrap gap-2 self-start justify-end">
            <button onClick={downloadContract} data-testid="profile-contract-pdf" className="flex items-center gap-2 border border-[#16345c] text-[#16345c] hover:bg-[#16345c] hover:text-white font-bold text-[13px] px-4 py-2.5 rounded-sm transition-colors">
              <FileText size={16} /> Scheda contratto
            </button>
            <button onClick={downloadSeniority} data-testid="profile-seniority-pdf" className="flex items-center gap-2 border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white font-bold text-[13px] px-4 py-2.5 rounded-sm transition-colors">
              <History size={16} /> Riepilogo anzianità
            </button>
            {!isExternal && (
              <button onClick={downloadAttestato} disabled={downloadingAtt} data-testid="profile-attestato-servizio" className="flex items-center gap-2 border border-[#009640] text-[#009640] hover:bg-[#009640] hover:text-white font-bold text-[13px] px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60">
                <BadgeCheck size={16} /> {downloadingAtt ? "Generazione..." : "Scarica attestato di servizio"}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-[16px] font-bold text-[#16345c] mb-4 flex items-center gap-2"><User size={18} /> Dati di contatto</h2>
            <form onSubmit={saveProfile} className="space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-gray-600 mb-1">Email</label>
                <div className="relative"><Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="profile-email" className={inputCls} /></div>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-gray-600 mb-1">Telefono</label>
                <div className="relative"><Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone" placeholder="+39 ..." className={inputCls} /></div>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-gray-600 mb-1">Indirizzo</label>
                <div className="relative"><MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="profile-address" placeholder="Via, città" className={inputCls} /></div>
              </div>
              <button type="submit" disabled={savingProfile} data-testid="profile-save" className="w-full flex items-center justify-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold text-[14px] py-2.5 rounded-sm transition-colors disabled:opacity-60">
                <Save size={16} /> {savingProfile ? "Salvataggio..." : "Salva modifiche"}
              </button>
            </form>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-[16px] font-bold text-[#16345c] mb-4 flex items-center gap-2"><Lock size={18} /> Cambia password</h2>
            <form onSubmit={savePwd} className="space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-gray-600 mb-1">Password attuale</label>
                <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="pwd-current" className="w-full border border-gray-300 rounded-sm px-3 py-2.5 text-[14px] outline-none focus:border-[#16345c]" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-gray-600 mb-1">Nuova password</label>
                <input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="pwd-new" className="w-full border border-gray-300 rounded-sm px-3 py-2.5 text-[14px] outline-none focus:border-[#16345c]" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-gray-600 mb-1">Conferma nuova password</label>
                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="pwd-confirm" className="w-full border border-gray-300 rounded-sm px-3 py-2.5 text-[14px] outline-none focus:border-[#16345c]" />
              </div>
              <button type="submit" disabled={savingPwd} data-testid="pwd-save" className="w-full flex items-center justify-center gap-2 bg-[#e30613] hover:bg-[#c00510] text-white font-bold text-[14px] py-2.5 rounded-sm transition-colors disabled:opacity-60">
                <Lock size={16} /> {savingPwd ? "Aggiornamento..." : "Aggiorna password"}
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfiloPage;
